import type { Snapshot, SnapshotProcess } from '@legalhermes/shared/domain';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Bell,
  Check,
  ChevronDown,
  Copy,
  FileText,
  Home,
  Lock,
} from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { queryKeys } from '../lib/queryKeys';
import { cn } from '../lib/utils';
import { api } from '../services/api';
import { formatDate, formatDateLong, formatDateTime, formatRelativeToNow } from '../utils/dates';
import {
  buildCaseTitle,
  getProcessStatusKey,
  groupActuacionesByYear,
  type Party,
  PROCESS_STATUS,
  roleExplanation,
  roleLabel,
  sortActuacionesDesc,
  sortByLastActuacionDesc,
  toParties,
} from './cases/cases.helpers';

const sectionTitleClass = 'font-serif text-[1.3125rem] font-semibold text-ink tracking-tight';

const ACTUACIONES_PER_PAGE = 20;

const backButtonClass =
  'inline-flex items-center gap-2 px-4 py-2 min-h-11 border border-border-emphasis rounded-sm text-[0.8125rem] font-medium text-ink-secondary bg-transparent cursor-pointer hover:text-ink hover:border-border-strong transition-colors duration-150';

interface BackToCasesProps {
  navigate: (path: string) => void;
  className?: string;
}

function BackToCases({ navigate, className }: BackToCasesProps) {
  return (
    <button type="button" className={cn(backButtonClass, className)} onClick={() => navigate('/')}>
      <ArrowLeft size={13} /> Volver a mis casos
    </button>
  );
}

interface DetailErrorProps {
  title: string;
  children: ReactNode;
  navigate: (path: string) => void;
}

function DetailError({ title, children, navigate }: DetailErrorProps) {
  return (
    <div className="mb-8">
      <h2 className="flex items-center gap-2 mb-4">
        <AlertCircle size={24} /> {title}
      </h2>
      <p>{children}</p>
      <BackToCases navigate={navigate} className="mt-4" />
    </div>
  );
}

interface StatusPillProps {
  process: SnapshotProcess;
}

function StatusPill({ process }: StatusPillProps) {
  const status = PROCESS_STATUS[getProcessStatusKey(process)];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-[0.4375rem] px-2.5 py-1 rounded-pill border text-[0.875rem] font-semibold',
        status.pillClass
      )}
    >
      <span className={cn('w-[0.4375rem] h-[0.4375rem] rounded-full', status.dotClass)} />
      {status.text}
    </span>
  );
}

function PrivateBadge() {
  return (
    <span className="inline-flex items-center gap-[0.4375rem] px-2.5 py-1 rounded-pill border text-[0.875rem] font-semibold text-ink-secondary border-border-emphasis bg-surface-raised">
      <Lock size={13} aria-hidden="true" />
      Privado
    </span>
  );
}

interface SummaryGridProps {
  items: Array<{ label: string; value: string }>;
}

function SummaryGrid({ items }: SummaryGridProps) {
  return (
    <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-px bg-border rounded-sm overflow-hidden border border-border">
      {items.map(({ label, value }) => (
        <div key={label} className="bg-surface px-4 py-3.5">
          <div className="text-[0.8125rem] text-ink-muted">{label}</div>
          <div className="text-[1rem] font-semibold text-ink mt-px">{value}</div>
        </div>
      ))}
    </div>
  );
}

interface LatestMovementProps {
  actuacion: { fechaActuacion: string | null; actuacion: string | null; anotacion: string | null };
}

function LatestMovement({ actuacion }: LatestMovementProps) {
  const relative = formatRelativeToNow(actuacion.fechaActuacion);
  return (
    <div className="mt-5 bg-surface border border-accent/35 rounded-sm p-[1.125rem]">
      <div className="flex items-center gap-2">
        <Bell size={15} className="text-accent flex-shrink-0" />
        <span className="text-[0.8125rem] font-semibold uppercase tracking-[0.05em] text-accent">
          Lo último que pasó
        </span>
      </div>
      <p className="mt-2.5 font-serif text-[1.25rem] sm:text-[1.4375rem] font-semibold leading-snug text-ink">
        {actuacion.actuacion || 'Actuación sin descripción'}
      </p>
      {actuacion.anotacion && (
        <p className="mt-1.5 text-[0.9375rem] text-ink-secondary">{actuacion.anotacion}</p>
      )}
      <p className="mt-2.5 text-[0.9375rem] text-ink-muted first-letter:uppercase">
        {formatDateLong(actuacion.fechaActuacion)}
        {relative && ` · ${relative}`}
      </p>
    </div>
  );
}

interface PartiesListProps {
  parties: Party[];
}

function PartiesList({ parties }: PartiesListProps) {
  if (parties.length === 0) return null;

  return (
    <>
      <h2 className={cn(sectionTitleClass, 'mt-9')}>Quiénes participan</h2>
      <div className="mt-3 flex flex-col gap-2.5">
        {parties.map((party, index) => (
          <div
            key={`${party.tipo}-${party.nombre}-${index}`}
            className="flex gap-3 items-center bg-surface border border-border rounded-sm px-4 py-3.5"
          >
            <span
              className={cn(
                'w-[2.125rem] h-[2.125rem] rounded-full flex-shrink-0 flex items-center justify-center text-[0.875rem] font-bold leading-none',
                party.side === 'demandante'
                  ? 'bg-accent-subtle text-accent'
                  : 'bg-ink/[0.07] text-ink-secondary'
              )}
            >
              {party.inicial}
            </span>
            <div className="min-w-0">
              <div className="text-[0.8125rem] text-ink-muted">{roleLabel(party)}</div>
              <div className="text-[1.03125rem] font-semibold text-ink leading-snug">
                {party.nombre}
              </div>
              <div className="text-[0.875rem] text-ink-muted mt-px">
                {roleExplanation(party.side)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

interface ExpedienteAccordionProps {
  fields: Array<{ label: string; value: string; mono?: boolean }>;
  radicado: string | null;
}

function ExpedienteAccordion({ fields, radicado }: ExpedienteAccordionProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!radicado) return;
    try {
      await navigator.clipboard.writeText(radicado);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="mt-3.5 border border-border rounded-sm overflow-hidden bg-surface">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3.5 min-h-11 bg-transparent border-none cursor-pointer text-left hover:bg-surface-raised transition-colors duration-150"
      >
        <span className="text-[1rem] font-medium text-ink">Datos del expediente</span>
        <span className="flex items-center gap-2 text-ink-muted text-[0.875rem] flex-shrink-0">
          {open ? 'Ocultar' : 'Ver'}
          <ChevronDown
            size={15}
            className={cn('transition-transform duration-150', open && 'rotate-180')}
          />
        </span>
      </button>

      {open && (
        <div className="border-t border-border px-4 pb-3.5 pt-1.5 bg-surface-inset">
          {fields.map(({ label, value, mono }) => (
            <div key={label} className="flex gap-3.5 flex-wrap py-2.5 border-b border-border">
              <span className="text-[0.875rem] text-ink-muted w-36 flex-shrink-0">{label}</span>
              <span
                className={cn(
                  'text-[0.9375rem] text-ink min-w-0 break-all',
                  mono && 'font-mono tracking-wide'
                )}
              >
                {value}
              </span>
            </div>
          ))}
          {radicado && (
            <button
              onClick={copy}
              className="inline-flex items-center gap-2 mt-3.5 px-3.5 py-2.5 min-h-11 bg-transparent border border-border-emphasis rounded-sm text-ink-secondary text-[0.875rem] font-medium cursor-pointer hover:text-ink hover:border-border-strong transition-colors duration-150"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Radicado copiado' : 'Copiar el radicado'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

type PersonData = Snapshot & { type: 'person' };

interface ProcessData {
  type: 'process';
  searchDate: string | null;
  extractedAt: string | null;
  processDetails: SnapshotProcess;
  version: number;
}

type ViewData = PersonData | ProcessData;
type ViewError = 'not_found' | 'invalid_type' | null;

function DetailView() {
  const { type, id } = useParams<{ type: string; id: string }>();
  return <DetailViewContent type={type} id={id} />;
}

interface DetailViewContentProps {
  type: string | undefined;
  id: string | undefined;
}

function DetailViewContent({ type, id }: DetailViewContentProps) {
  const navigate = useNavigate();

  const {
    data: nameData,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: queryKeys.historicalByName,
    queryFn: api.getHistoricalDataByName,
  });

  const { data, error }: { data: ViewData | null; error: ViewError } = useMemo(() => {
    if (!nameData || !id) return { data: null, error: null };

    if (type === 'nombre') {
      const decodedName = decodeURIComponent(id).toLowerCase().trim();
      const personData = nameData[decodedName];
      if (!personData) return { data: null, error: 'not_found' as const };
      return { data: { type: 'person', ...personData } as PersonData, error: null };
    }

    if (type === 'proceso') {
      for (const personData of Object.values(nameData)) {
        if (personData.processes && Array.isArray(personData.processes)) {
          const foundProcess = personData.processes.find((p) => String(p.idProceso) === id);
          if (foundProcess) {
            return {
              data: {
                type: 'process',
                searchDate: personData.searchDate,
                extractedAt: personData.extractedAt,
                processDetails: foundProcess,
                version: personData.version,
              } as ProcessData,
              error: null,
            };
          }
        }
      }
      return { data: null, error: 'not_found' as const };
    }

    return { data: null, error: 'invalid_type' as const };
  }, [nameData, type, id]);

  if (isLoading) {
    return (
      <div className="mb-8 text-center py-12">
        <p>Cargando detalles...</p>
      </div>
    );
  }

  if (queryError) {
    return (
      <DetailError title="Error" navigate={navigate}>
        Ocurrió un error al cargar los datos: {queryError.message}
      </DetailError>
    );
  }

  if (error === 'not_found') {
    return (
      <DetailError title="No Encontrado" navigate={navigate}>
        El {type === 'nombre' ? 'persona' : 'proceso'} "{decodeURIComponent(id ?? '')}" no existe en
        los datos históricos.
      </DetailError>
    );
  }

  if (error === 'invalid_type') {
    return (
      <DetailError title="Tipo Inválido" navigate={navigate}>
        El tipo "{type}" no es válido. Use "nombre" o "proceso".
      </DetailError>
    );
  }

  if (data && data.type === 'person') {
    return <PersonView data={data} navigate={navigate} />;
  }

  if (data && data.type === 'process') {
    // key: remonta al cambiar de proceso para reiniciar la paginación.
    return <ProcessView key={id} data={data} id={id} navigate={navigate} />;
  }

  return null;
}

interface PersonViewProps {
  data: PersonData;
  navigate: (path: string) => void;
}

function PersonView({ data, navigate }: PersonViewProps) {
  const sortedProcesses = sortByLastActuacionDesc(data.processes || []);

  return (
    <div>
      <Breadcrumb type="nombre" id={data.searchName} navigate={navigate} />

      <div className="text-[0.8125rem] font-semibold uppercase tracking-[0.07em] text-ink-muted">
        Casos de
      </div>
      <h1 className="mt-1.5 font-serif text-[1.5625rem] sm:text-[1.9375rem] font-semibold leading-tight tracking-tight text-ink capitalize">
        {data.searchName}
      </h1>
      <div className="flex items-center gap-2 flex-wrap mt-3">
        <span className="text-[0.875rem] text-ink-secondary">
          {data.totalResults}{' '}
          {data.totalResults === 1 ? 'proceso encontrado' : 'procesos encontrados'}
        </span>
        <span className="text-[0.875rem] text-ink-muted">
          · Actualizado {formatDateTime(data.extractedAt)}
        </span>
      </div>

      <h2 className={cn(sectionTitleClass, 'mt-8')}>Sus procesos</h2>

      {sortedProcesses.length > 0 ? (
        <div className="mt-3 flex flex-col gap-2.5">
          {sortedProcesses.map((process) => {
            const title = buildCaseTitle(toParties(process.sujetos || []), process.despacho);
            const Row = process.esPrivado ? 'div' : 'button';
            return (
              <Row
                key={process.idProceso}
                {...(process.esPrivado
                  ? {}
                  : {
                      type: 'button' as const,
                      onClick: () => navigate(`/detalle/proceso/${process.idProceso}`),
                    })}
                className={cn(
                  'w-full text-left flex items-start gap-3 bg-surface border border-border rounded-sm px-4 py-3.5 transition-colors duration-150 group',
                  !process.esPrivado && 'cursor-pointer hover:bg-surface-raised'
                )}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[1.03125rem] font-semibold text-ink leading-snug">
                    {title.demandante}
                    {title.demandado && (
                      <>
                        <span className="text-ink-muted font-medium"> contra </span>
                        {title.demandado}
                      </>
                    )}
                  </div>

                  {!title.fallback && process.despacho && (
                    <div className="text-[0.875rem] text-ink-muted mt-0.5">{process.despacho}</div>
                  )}

                  <div className="flex items-center gap-2 flex-wrap mt-2">
                    <StatusPill process={process} />
                    {process.esPrivado && <PrivateBadge />}
                    {process.fechaUltimaActuacion && (
                      <span className="text-[0.875rem] text-ink-muted">
                        Último movimiento {formatDate(process.fechaUltimaActuacion)}
                      </span>
                    )}
                  </div>
                </div>

                {!process.esPrivado && (
                  <ArrowRight
                    size={15}
                    className="text-ink-muted group-hover:text-accent flex-shrink-0 mt-1 transition-colors duration-150"
                  />
                )}
              </Row>
            );
          })}
        </div>
      ) : (
        <p className="text-center text-ink-muted py-8">
          Esta persona no tiene procesos registrados.
        </p>
      )}

      <BackToCases navigate={navigate} className="mt-9" />
    </div>
  );
}

interface ProcessViewProps {
  data: ProcessData;
  id: string | undefined;
  navigate: (path: string) => void;
}

function ProcessView({ data, id, navigate }: ProcessViewProps) {
  const [actuacionesPage, setActuacionesPage] = useState(1);

  const processDetails = data.processDetails;
  // Ordenar todas las actuaciones es O(n log n) sobre cientos de items; sin memo
  // se repetiría en cada clic de paginación, que no cambia el orden.
  const sortedActuaciones = useMemo(
    () => sortActuacionesDesc(processDetails.actuaciones || []),
    [processDetails]
  );

  const parties = toParties(processDetails.sujetos || []);

  const totalActuaciones = sortedActuaciones.length;
  const totalPages = Math.ceil(totalActuaciones / ACTUACIONES_PER_PAGE);
  const startIndex = (actuacionesPage - 1) * ACTUACIONES_PER_PAGE;
  const endIndex = startIndex + ACTUACIONES_PER_PAGE;
  const currentActuaciones = sortedActuaciones.slice(startIndex, endIndex);
  const groupedActuaciones = groupActuacionesByYear(currentActuaciones);
  const latest = sortedActuaciones[0];
  const currentYear = new Date().getFullYear();

  const title = buildCaseTitle(parties, processDetails.despacho);

  const summary = [
    { label: 'Empezó el', value: formatDate(processDetails.fechaRadicacion) },
    {
      label: 'Último movimiento',
      value: processDetails.fechaUltimaActuacion
        ? formatDate(processDetails.fechaUltimaActuacion)
        : 'Sin actuaciones',
    },
    {
      label: 'Movimientos registrados',
      value: `${processDetails.totalActuaciones ?? totalActuaciones} en total`,
    },
    ...(processDetails.ubicacion
      ? [{ label: 'Dónde está ahora', value: processDetails.ubicacion }]
      : []),
    ...(processDetails.tipoProceso
      ? [{ label: 'Tipo de proceso', value: processDetails.tipoProceso }]
      : []),
    ...(processDetails.claseProceso
      ? [{ label: 'Clase', value: processDetails.claseProceso }]
      : []),
    ...(processDetails.subclaseProceso
      ? [{ label: 'Por', value: processDetails.subclaseProceso }]
      : []),
    ...(processDetails.ponente ? [{ label: 'Juez a cargo', value: processDetails.ponente }] : []),
  ];

  const expedienteFields = [
    ...(processDetails.numeroRadicacion
      ? [
          {
            label: 'Número de radicado',
            value: processDetails.numeroRadicacion,
            mono: true,
          },
        ]
      : []),
    ...(processDetails.despacho ? [{ label: 'Juzgado', value: processDetails.despacho }] : []),
    ...(processDetails.departamento
      ? [{ label: 'Departamento', value: processDetails.departamento }]
      : []),
    ...(processDetails.recurso && processDetails.recurso !== 'Sin Tipo de Recurso'
      ? [{ label: 'Recurso', value: processDetails.recurso }]
      : []),
    {
      label: 'Consulta pública',
      value: processDetails.esPrivado
        ? 'No, es un proceso privado'
        : 'Sí, cualquiera puede consultarlo',
    },
    { label: 'Búsqueda realizada', value: formatDate(data.searchDate) },
    { label: 'Datos extraídos', value: formatDateTime(data.extractedAt) },
  ];

  return (
    <div>
      <Breadcrumb type="proceso" id={id ?? ''} navigate={navigate} />

      {title.fallback ? (
        <h1 className="font-serif text-[1.5625rem] sm:text-[1.9375rem] font-semibold leading-tight tracking-tight text-ink">
          {title.demandante}
        </h1>
      ) : (
        <>
          {processDetails.subclaseProceso && (
            <div className="text-[0.8125rem] font-semibold uppercase tracking-[0.07em] text-ink-muted">
              {processDetails.subclaseProceso}
            </div>
          )}
          <h1 className="mt-1.5 font-serif text-[1.5625rem] sm:text-[1.9375rem] font-semibold leading-tight tracking-tight text-ink">
            {title.demandante}
            {title.demandado && (
              <>
                <span className="text-ink-muted font-medium"> contra </span>
                {title.demandado}
              </>
            )}
          </h1>
        </>
      )}

      <div className="flex items-center gap-2 flex-wrap mt-3">
        <StatusPill process={processDetails} />
        {processDetails.esPrivado && <PrivateBadge />}
        {!title.fallback && processDetails.despacho && (
          <span className="text-[0.875rem] text-ink-muted">{processDetails.despacho}</span>
        )}
      </div>

      {latest && <LatestMovement actuacion={latest} />}

      <h2 className={cn(sectionTitleClass, 'mt-8')}>En qué va el proceso</h2>
      <SummaryGrid items={summary} />

      <PartiesList parties={parties} />

      <ExpedienteAccordion fields={expedienteFields} radicado={processDetails.numeroRadicacion} />

      {totalActuaciones > 0 && (
        <div className="mt-9">
          <div className="flex justify-between items-baseline gap-3 flex-wrap">
            <h2 className={sectionTitleClass}>Todo lo que ha pasado</h2>
            <span className="text-[0.875rem] text-ink-muted">
              {totalPages > 1
                ? `${startIndex + 1}–${Math.min(endIndex, totalActuaciones)} de ${totalActuaciones}`
                : `${totalActuaciones} ${totalActuaciones === 1 ? 'movimiento' : 'movimientos'}`}
            </span>
          </div>
          <p className="mt-1 text-[0.90625rem] text-ink-muted">De lo más nuevo a lo más antiguo.</p>

          <div className="mt-4">
            {groupedActuaciones.map((group) => (
              <div key={group.year ?? 'sin-fecha'} className="mb-5">
                <div className="flex items-center gap-3 mb-1.5">
                  <span className="text-[0.8125rem] font-bold tracking-[0.09em] text-ink-muted">
                    {group.year === null
                      ? 'SIN FECHA'
                      : group.year === currentYear
                        ? `ESTE AÑO — ${group.year}`
                        : String(group.year)}
                  </span>
                  <span className="flex-1 h-px bg-border" />
                </div>

                <div className="flex flex-col">
                  {group.items.map((actuacion) => (
                    <div
                      key={actuacion.idRegActuacion}
                      className="flex gap-3.5 py-3.5 border-b border-border"
                    >
                      <div className="w-[0.5625rem] flex-shrink-0 flex flex-col items-center pt-[0.4375rem]">
                        <span className="w-[0.5625rem] h-[0.5625rem] rounded-full bg-surface-raised border border-border-strong" />
                        <span className="flex-1 w-px bg-border mt-1.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-[0.875rem] text-ink-muted tabular-nums">
                            {formatDate(actuacion.fechaActuacion)}
                          </span>
                          {actuacion.conDocumentos && (
                            <span className="text-[0.6875rem] px-1.5 py-px rounded border border-success/30 text-success bg-success-subtle inline-flex items-center gap-1">
                              <FileText size={10} /> Docs
                            </span>
                          )}
                        </div>
                        <div className="text-[1.03125rem] font-medium text-ink leading-snug mt-px">
                          {actuacion.actuacion || 'Sin descripción'}
                        </div>
                        {actuacion.anotacion && (
                          <div className="text-[0.875rem] text-ink-tertiary italic leading-snug mt-0.5">
                            {actuacion.anotacion}
                          </div>
                        )}
                        {actuacion.fechaRegistro &&
                          actuacion.fechaRegistro !== actuacion.fechaActuacion && (
                            <div className="text-[0.8125rem] text-ink-muted mt-0.5">
                              Registrado el {formatDate(actuacion.fechaRegistro)}
                            </div>
                          )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 ? (
            <div className="flex justify-between gap-2 mt-4 pt-4 border-t border-border items-center">
              <button
                className="px-4 py-2 min-h-11 border border-border-emphasis rounded-sm text-[0.8125rem] font-medium font-sans inline-flex items-center gap-2 cursor-pointer bg-transparent text-ink-secondary hover:text-ink hover:border-border-strong transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
                onClick={() => setActuacionesPage((prev) => Math.max(1, prev - 1))}
                disabled={actuacionesPage === 1}
              >
                <ArrowLeft size={13} /> Anterior
              </button>
              <span className="text-[0.8125rem] text-ink-muted">
                {actuacionesPage} / {totalPages}
              </span>
              <button
                className="px-4 py-2 min-h-11 border border-border-emphasis rounded-sm text-[0.8125rem] font-medium font-sans inline-flex items-center gap-2 cursor-pointer bg-transparent text-ink-secondary hover:text-ink hover:border-border-strong transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
                onClick={() => setActuacionesPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={actuacionesPage === totalPages}
              >
                Siguiente <ArrowRight size={13} />
              </button>
            </div>
          ) : (
            <p className="text-center text-[0.875rem] text-ink-muted pt-2">
              Aquí empieza el proceso. No hay nada anterior.
            </p>
          )}
        </div>
      )}

      <BackToCases navigate={navigate} className="mt-9" />
    </div>
  );
}

interface BreadcrumbProps {
  type: 'nombre' | 'proceso';
  id: string;
  navigate: (path: string) => void;
}

function Breadcrumb({ id, navigate }: BreadcrumbProps) {
  const label = decodeURIComponent(id);
  const truncated = label.length > 28 ? `${label.slice(0, 28)}…` : label;
  return (
    <div className="mb-4 text-[0.8125rem] text-ink-muted flex items-center flex-wrap gap-1.5">
      <button
        type="button"
        onClick={() => navigate('/')}
        className="p-0 bg-transparent border-none font-sans text-[0.8125rem] cursor-pointer text-ink-secondary inline-flex items-center gap-1 hover:text-ink transition-colors duration-150"
      >
        <Home size={13} /> Mis casos
      </button>
      <span className="text-ink-muted/40">›</span>
      <span className="text-ink-muted font-mono text-[0.75rem]" title={label}>
        {truncated}
      </span>
    </div>
  );
}

export default DetailView;
